import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminInstance, InviteCode, PublicUser } from '@hub/shared';
import { api } from '../../api/client';
import {
  ActionButton, Alert, Card, EmptyRow, Loading, Pill, SectionTitle, StateDot, fmtDate, useFlash,
} from '../../components/ui';

interface Overview {
  users: PublicUser[];
  invites: InviteCode[];
  instances: AdminInstance[];
  caddyAuth: { mode: 'global' | 'admin_only' | 'none'; username: string };
}

export function AdminPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Overview>('/api/admin/overview'));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the admin overview');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (error) return <Alert tone="error">{error}</Alert>;
  if (!data) return <Loading />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Admin</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to="/admin/docker" className="btn-ghost" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}>Docker →</Link>
          <Link to="/admin/caddy" className="btn-ghost" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}>Caddy →</Link>
          <Link to="/admin/services" className="btn-ghost" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}>Services →</Link>
        </div>
      </div>

      <UsersCard users={data.users} onChanged={load} />
      <InvitesCard invites={data.invites} onChanged={load} />
      <InstancesCard instances={data.instances} onChanged={load} />
      <ServiceDeploymentCard />
      <CaddyAuthCard initial={data.caddyAuth} onChanged={load} />
    </>
  );
}

/** Whether non-admins may deploy a compose file they write themselves. */
function ServiceDeploymentCard() {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const flash = useFlash();

  useEffect(() => {
    api.get<{ allowUserCompose: boolean }>('/api/admin/settings/services')
      .then((r) => setAllowed(r.allowUserCompose))
      .catch(() => setAllowed(false));
  }, []);

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Service deployment</SectionTitle>
      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
        Catalog products are always available. This controls whether non-admin users may also paste their own
        compose file. Submitted stacks are screened: no privileged containers, host bind mounts, host networking,
        or self-published ports.
      </p>
      {allowed === null ? <Loading /> : (
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={allowed} onChange={(e) => setAllowed(e.target.checked)} />
            Let users deploy their own compose file
          </label>
          <ActionButton
            className="btn-primary" busyLabel="Saving…"
            onClick={async () => {
              await api.put('/api/admin/settings/services', { allowUserCompose: allowed });
              flash.show('Saved', 'ok');
            }}
          >Save</ActionButton>
        </div>
      )}
    </Card>
  );
}

function UsersCard({ users, onChanged }: { users: PublicUser[]; onChanged: () => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const flash = useFlash();

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Users</SectionTitle>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div style={{ flex: '1 1 14rem' }}>
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 10rem' }}>
          <label className="form-label">Password</label>
          <input className="form-input" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div style={{ flex: '0 1 8rem' }}>
          <label className="form-label">Role</label>
          <select className="form-input" value={role} onChange={(e) => setRole(e.target.value as 'user' | 'admin')}>
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
        </div>
        <ActionButton
          className="btn-primary" busyLabel="Creating…"
          disabled={!email || password.length < 8}
          onClick={async () => {
            await api.post('/api/admin/users', { email, password, role });
            setEmail(''); setPassword('');
            flash.show('User created (email pre-verified)', 'ok');
            await onChanged();
          }}
        >Add user</ActionButton>
      </div>

      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr><th>Username</th><th>Email</th><th>Role</th><th>Verified</th><th>2FA</th><th>Max instances</th><th>Created</th></tr>
          </thead>
          <tbody>
            {users.length === 0 ? <EmptyRow colSpan={7}>No users.</EmptyRow> : users.map((u) => (
              <UserRow key={u.id} user={u} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function UserRow({ user, onChanged }: { user: PublicUser; onChanged: () => Promise<void> }) {
  const [maxInstances, setMaxInstances] = useState(user.maxInstances === null ? '' : String(user.maxInstances));

  return (
    <tr>
      <td className="mono">{user.username}</td>
      <td className="muted">{user.email}</td>
      <td>
        <select
          className="form-input"
          style={{ width: '6.5rem', padding: '0.15rem 0.4rem', fontSize: '0.75rem' }}
          value={user.role}
          onChange={(e) => {
            void api.patch(`/api/admin/users/${user.id}`, { role: e.target.value }).then(onChanged);
          }}
        >
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
      </td>
      <td><Pill tone={user.emailVerified ? 'emerald' : 'muted'}>{user.emailVerified ? 'yes' : 'no'}</Pill></td>
      <td><Pill tone={user.twoFactorEnabled ? 'emerald' : 'muted'}>{user.twoFactorEnabled ? 'on' : 'off'}</Pill></td>
      <td>
        <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
          <input
            className="form-input"
            style={{ width: '4.5rem', padding: '0.15rem 0.4rem', fontSize: '0.75rem' }}
            placeholder="default"
            value={maxInstances}
            onChange={(e) => setMaxInstances(e.target.value)}
          />
          <ActionButton
            busyLabel="…"
            onClick={async () => {
              // Blank resets to the global default
              const value = maxInstances.trim() === '' ? null : Number(maxInstances);
              await api.patch(`/api/admin/users/${user.id}`, { maxInstances: value });
              await onChanged();
            }}
          >Set</ActionButton>
        </div>
      </td>
      <td className="muted">{fmtDate(user.createdAt)}</td>
    </tr>
  );
}

function InvitesCard({ invites, onChanged }: { invites: InviteCode[]; onChanged: () => Promise<void> }) {
  const [maxUses, setMaxUses] = useState('');
  const flash = useFlash();

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Invite codes</SectionTitle>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
        <div style={{ flex: '0 1 10rem' }}>
          <label className="form-label">Max uses (blank = unlimited)</label>
          <input className="form-input" type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
        </div>
        <ActionButton
          className="btn-primary" busyLabel="Generating…"
          onClick={async () => {
            const res = await api.post<{ code: string }>('/api/admin/invites', maxUses ? { maxUses: Number(maxUses) } : {});
            setMaxUses('');
            flash.show(`Created ${res.code}`, 'ok');
            await onChanged();
          }}
        >Generate</ActionButton>
      </div>

      <div className="scroll-x">
        <table className="data">
          <thead><tr><th>Code</th><th>Uses</th><th>Expires</th></tr></thead>
          <tbody>
            {invites.length === 0 ? <EmptyRow colSpan={3}>No invite codes.</EmptyRow> : invites.map((i) => (
              <tr key={i.id}>
                <td className="mono">{i.code}</td>
                <td className="muted">{i.use_count}{i.max_uses !== null ? ` / ${i.max_uses}` : ''}</td>
                <td className="muted">{i.expires_at ? fmtDate(i.expires_at) : 'never'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function InstancesCard({ instances, onChanged }: { instances: AdminInstance[]; onChanged: () => Promise<void> }) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expiresAt, setExpiresAt] = useState('');
  const flash = useFlash();

  const ids = useMemo(() => [...selected], [selected]);
  const allSelected = instances.length > 0 && selected.size === instances.length;

  const toggle = (id: number) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /** Bulk endpoints report per-item failures rather than failing the batch. */
  const runBulk = async (path: string, extra?: Record<string, unknown>) => {
    const res = await api.post<{ processed: number; errors?: { id: number; error: string }[] }>(
      path, { ids, ...(extra ?? {}) },
    );
    const failed = res.errors?.length ?? 0;
    flash.show(
      failed ? `${res.processed} done, ${failed} failed — ${res.errors![0]!.error}` : `${res.processed} done`,
      failed ? 'warn' : 'ok',
    );
    setSelected(new Set());
    await onChanged();
  };

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>All instances</SectionTitle>

      {selected.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
          marginBottom: '0.75rem', padding: '0.5rem 0.75rem',
          background: 'var(--ha-bg-elevated)', borderRadius: '0.375rem',
        }}>
          <span className="muted" style={{ fontSize: '0.8rem' }}>{selected.size} selected</span>
          <ActionButton busyLabel="Restarting…" onClick={() => runBulk('/api/admin/instances/bulk-restart')}>Restart</ActionButton>
          <ActionButton
            danger busyLabel="Deleting…"
            confirm={`Permanently delete ${selected.size} instance(s)? This cannot be undone.`}
            onClick={() => runBulk('/api/admin/instances/bulk-delete')}
          >Delete</ActionButton>
          <input
            className="form-input" type="datetime-local"
            style={{ width: '14rem', padding: '0.15rem 0.4rem', fontSize: '0.75rem' }}
            value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
          />
          <ActionButton busyLabel="Saving…" onClick={() => runBulk('/api/admin/instances/bulk-expiration', { expiresAt })}>
            Set expiry
          </ActionButton>
        </div>
      )}

      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox" checked={allSelected}
                  onChange={(e) => setSelected(e.target.checked ? new Set(instances.map((i) => i.id)) : new Set())}
                />
              </th>
              <th>Name</th><th>Subdomain</th><th>Status</th><th>Port</th>
              <th>CPU</th><th>RAM</th><th>Disk</th><th>Expires</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {instances.length === 0 ? <EmptyRow colSpan={10}>No instances.</EmptyRow> : instances.map((i) => (
              <tr key={i.id}>
                <td><input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} /></td>
                <td>{i.name}</td>
                <td className="mono">{i.subdomain}</td>
                <td><StateDot state={i.status} /></td>
                <td className="mono muted">{i.host_port ?? '-'}</td>
                <td className="muted">{i.cpuPercent === null ? '-' : `${i.cpuPercent}%`}</td>
                <td className="muted">{i.ramMb === null ? '-' : `${i.ramMb} MB`}</td>
                <td className="muted">{i.diskMb === null ? '-' : `${i.diskMb} MB`}</td>
                {/* expires_at is stored in seconds, unlike the millisecond timestamps */}
                <td className="muted">{i.expires_at ? fmtDate(i.expires_at * 1000) : 'never'}</td>
                <td>
                  <ActionButton
                    busyLabel="Issuing…"
                    onClick={() => api.post(`/api/admin/instances/${i.id}/reissue-cert`)}
                  >Reissue cert</ActionButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CaddyAuthCard({ initial, onChanged }: {
  initial: { mode: 'global' | 'admin_only' | 'none'; username: string };
  onChanged: () => Promise<void>;
}) {
  const [mode, setMode] = useState(initial.mode);
  const [username, setUsername] = useState(initial.username);
  const [password, setPassword] = useState('');
  const flash = useFlash();

  return (
    <Card>
      <SectionTitle actions={flash.node}>Hub Caddy auth</SectionTitle>
      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
        HTTP basic auth applied by Caddy in front of the hub itself. Leave the password blank to keep the current one.
      </p>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {(['none', 'admin_only', 'global'] as const).map((m) => (
          <label key={m} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
            <input type="radio" name="caddy-mode" checked={mode === m} onChange={() => setMode(m)} />
            {m === 'none' ? 'No auth' : m === 'admin_only' ? 'Protect /admin only' : 'Protect everything'}
          </label>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 10rem' }}>
          <label className="form-label">Username</label>
          <input className="form-input" value={username} onChange={(e) => setUsername(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 10rem' }}>
          <label className="form-label">New password</label>
          <input className="form-input" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <ActionButton
          className="btn-primary" busyLabel="Applying…"
          onClick={async () => {
            await api.put('/api/admin/settings/caddy-auth', { mode, username, password });
            setPassword('');
            flash.show('Caddy auth applied', 'ok');
            await onChanged();
          }}
        >Apply</ActionButton>
      </div>
    </Card>
  );
}
